import {
  PublicClientApplication,
  AccountInfo,
  AuthenticationResult,
  PopupRequest,
  SilentRequest,
  EndSessionRequest,
} from '@azure/msal-browser';
import type { User, AuthResult, IAuthProvider, LogoutOptions } from '../../types/auth.types';
import type { AuthConfig } from '../config';
import {
  getInteractiveAuthScopes,
  isInteractionRequiredError,
  isRecoverableInteractionStateError,
} from '../authErrorUtils';

interface IdTokenClaims {
  tid?: string;
  extension_TenantId?: string;
  roles?: string[];
  extension_Permissions?: string[];
  extension_BankId?: string;
  picture?: string;
  [key: string]: unknown;
}

/**
 * Azure Entra External ID (CIAM) auth provider.
 *
 * Implements IAuthProvider so AuthContext never imports MSAL directly.
 * Key design points:
 *   - Uses `loginScopes` (openid/profile/email) for all token requests
 *   - Returns the **ID token** (not access token) for API calls, because
 *     CIAM External ID does not support self-referencing access tokens
 *     (AADSTS90009).  The ID token carries the correct aud/iss/sub claims
 *     that the Nexus gateway validates.
 *   - Falls back to popup if silent acquisition fails (e.g. consent required)
 */
export class AzureAuthProvider implements IAuthProvider {
  private static readonly TEMPORARY_REQUEST_CACHE_KEYS = [
    'request.params',
    'code.verifier',
    'request.origin',
    'urlHash',
    'request.native',
  ] as const;

  private config: AuthConfig;
  private msalInstance: PublicClientApplication;
  private account: AccountInfo | null = null;
  private initPromise: Promise<void> | null = null;

  constructor(config: AuthConfig) {
    this.config = config;

    const isProduction = import.meta.env.PROD;
    const logLevel = isProduction ? 0 : 3; // Error only in prod, Info in dev

    const msalConfig = {
      auth: {
        clientId: config.clientId,
        authority: config.authority,
        knownAuthorities: config.knownAuthorities,
        redirectUri: config.redirectUri,
        postLogoutRedirectUri: config.postLogoutRedirectUri,
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: 'sessionStorage' as const,
        storeAuthStateInCookie: false,
      },
      system: {
        loggerOptions: {
          logLevel,
          piiLoggingEnabled: false,
          loggerCallback(level: number, message: string, containsPii: boolean): void {
            if (!isProduction && !containsPii) {
              console.log('[MSAL]', message);
            }
          },
        },
      },
    };

    this.msalInstance = new PublicClientApplication(msalConfig);
    this.initPromise = this.msalInstance.initialize();
  }

  /* ------------------------------------------------------------------ */
  /* utilities                                                          */
  /* ------------------------------------------------------------------ */

  /** Decode a JWT and return its payload as an object. */
  private parseJwt(token: string): IdTokenClaims {
    if (!token) return {};
    try {
      const [, payload] = token.split('.');
      return JSON.parse(atob(payload));
    } catch {
      return {};
    }
  }

  private async ensureInitialized(): Promise<void> {
    if (this.initPromise) {
      await this.initPromise;
      this.initPromise = null;
    }
  }

  /* ------------------------------------------------------------------ */
  /* authentication flows                                               */
  /* ------------------------------------------------------------------ */

  async login(): Promise<AuthResult> {
    await this.ensureInitialized();

    const loginRequest: PopupRequest = {
      scopes: getInteractiveAuthScopes(this.config.loginScopes, this.config.apiScopes),
      prompt: 'select_account',
      extraQueryParameters: { ui_locales: 'en' },
    };

    const response = await this.acquireInteractiveToken(loginRequest);
    this.account = response.account ?? null;
    return this.processAuthResponse(response);
  }

  async loginSilent(): Promise<AuthResult> {
    await this.ensureInitialized();

    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      throw new Error('No accounts found');
    }
    this.account = accounts[0];

    return this.acquireApiToken();
  }

  async logout(options?: LogoutOptions): Promise<void> {
    if (options?.localOnly) {
      await this.ensureInitialized();
      await this.msalInstance.clearCache();
      this.account = null;
      return;
    }

    if (!this.account) return;

    await this.ensureInitialized();
    const account = this.account;
    const logoutRequest: EndSessionRequest = {
      account,
      postLogoutRedirectUri: this.config.postLogoutRedirectUri,
    };

    await this.msalInstance.logoutRedirect(logoutRequest);
    this.account = null;
  }

  async handleCallback(): Promise<AuthResult | null> {
    await this.ensureInitialized();

    const response = await this.msalInstance.handleRedirectPromise();
    if (response?.account) {
      this.account = response.account;
      if (response.accessToken) {
        return this.processAuthResponse(response);
      }

      return this.acquireApiToken();
    }
    return null;
  }

  /* ------------------------------------------------------------------ */
  /* token helpers                                                      */
  /* ------------------------------------------------------------------ */

  /**
   * Acquire an access token suitable for API calls.
   *
   * When `apiScopes` differ from `loginScopes`, MSAL will return an
   * access token scoped to your API (with the proper `aud` claim).
   * Falls back to popup if silent fails (e.g. first-time consent).
   */
  async getAccessToken(): Promise<string> {
    if (!this.account) {
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        throw new Error('No authenticated user');
      }
      this.account = accounts[0];
    }

    try {
      const response = await this.msalInstance.acquireTokenSilent({
        scopes: this.config.apiScopes,
        account: this.account,
      });

      // Prefer ID token (CIAM doesn't issue self-referencing access tokens)
      return response.idToken || response.accessToken;
    } catch (error) {
      if (!isInteractionRequiredError(error)) {
        throw error;
      }

      const response = await this.acquireInteractiveToken({
        scopes: this.config.apiScopes,
        account: this.account ?? undefined,
      });
      return response.idToken || response.accessToken;
    }
  }

  async refreshToken(): Promise<string> {
    return this.getAccessToken();
  }

  async isAuthenticated(): Promise<boolean> {
    await this.ensureInitialized();
    return this.msalInstance.getAllAccounts().length > 0;
  }

  /* ------------------------------------------------------------------ */
  /* user mapping                                                       */
  /* ------------------------------------------------------------------ */

  /**
   * Acquire an ID token suitable for API calls.
   *
   * CIAM External ID uses the ID token (not access token) as the bearer
   * token for API calls, since self-referencing access tokens are not
   * supported (AADSTS90009).
   */
  private async acquireApiToken(): Promise<AuthResult> {
    if (!this.account) {
      throw new Error('No account available after login');
    }

    const silentRequest: SilentRequest = {
      scopes: this.config.apiScopes,
      account: this.account,
    };

    let response: AuthenticationResult;
    try {
      response = await this.msalInstance.acquireTokenSilent(silentRequest);
    } catch (error) {
      if (!isInteractionRequiredError(error)) {
        throw error;
      }

      response = await this.acquireInteractiveToken({
        scopes: this.config.apiScopes,
        account: this.account ?? undefined,
      });
    }

    return this.processAuthResponse(response);
  }

  private async acquireInteractiveToken(
    request: PopupRequest,
    allowStaleStateRetry = true,
  ): Promise<AuthenticationResult> {
    try {
      return await this.msalInstance.acquireTokenPopup(request);
    } catch (error) {
      if (!allowStaleStateRetry || !isRecoverableInteractionStateError(error)) {
        throw error;
      }

      this.clearStaleInteractionState();
      return this.acquireInteractiveToken(request, false);
    }
  }

  private clearStaleInteractionState(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const interactionKey = 'msal.interaction.status';
    sessionStorage.removeItem(interactionKey);

    for (const key of AzureAuthProvider.TEMPORARY_REQUEST_CACHE_KEYS) {
      sessionStorage.removeItem(`msal.${this.config.clientId}.${key}`);
    }
  }

  private processAuthResponse(response: AuthenticationResult): AuthResult {
    const claims =
      (response.idTokenClaims as IdTokenClaims) ??
      this.parseJwt(response.idToken);
    const user = this.mapAccountToUser(response.account, claims);

    // Use the ID token for API calls — CIAM External ID does not issue
    // self-referencing access tokens (AADSTS90009), but the ID token
    // carries the correct aud (client ID) and iss (CIAM issuer) that the
    // Nexus gateway validates.
    const bearerToken = response.idToken || response.accessToken;

    return {
      user,
      accessToken: bearerToken,
      idToken: response.idToken ?? '',
      expiresAt: response.expiresOn?.getTime() ?? 0,
    };
  }

  private mapAccountToUser(account: AccountInfo, claims: IdTokenClaims = {}): User {
    return {
      id: account.localAccountId,
      email: account.username,
      name: account.name ?? '',
      tenantId: claims.tid ?? (claims.extension_TenantId as string) ?? '',
      role: ((claims.roles as string[])?.[0] ?? '') as User['role'],
      permissions: (claims.extension_Permissions as string[]) ?? [],
      bankId: claims.extension_BankId as string | undefined,
      avatar: claims.picture as string | undefined,
    };
  }

  async getUser(): Promise<User | null> {
    await this.ensureInitialized();

    if (!this.account) {
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length === 0) return null;
      this.account = accounts[0];
    }

    const claims =
      (this.account.idTokenClaims as IdTokenClaims) ??
      (await this.getIdTokenClaimsFromCache());

    return this.mapAccountToUser(this.account, claims);
  }

  /** Pull the latest ID-token from cache (or silently refresh). */
  private async getIdTokenClaimsFromCache(): Promise<IdTokenClaims> {
    try {
      const silentResponse = await this.msalInstance.acquireTokenSilent({
        scopes: this.config.loginScopes,
        account: this.account!,
        forceRefresh: false,
      });
      return (
        (silentResponse.idTokenClaims as IdTokenClaims) ??
        this.parseJwt(silentResponse.accessToken)
      );
    } catch {
      return {};
    }
  }
}
